import { useGet } from 'lib/api';
import { checkFeatureFlag } from '../utils';
import { apiUsers } from 'api/users';
import { FeatureFlags } from '../types';

export const useFeatureFlag = (feature: string) => {
  // allowAnonymous: feature flags are public and must also resolve for logged-out
  // visitors — public routes like /supplier/signup gate their whole page on a flag
  // (DL-61837: the federated-auth query gate otherwise blanks them).
  const { data } = useGet<FeatureFlags>(
    apiUsers.get.featureFlags,
    'feature-flags',
    undefined,
    undefined,
    false,
    {},
    true
  );
  return checkFeatureFlag(feature, data);
};

/**
 * Hook that returns both the feature flag value and loading state
 * Use this when you need to distinguish between "flag is false" and "flag is still loading"
 */
export const useFeatureFlagWithLoading = (feature: string) => {
  const { data, isPending } = useGet<FeatureFlags>(
    apiUsers.get.featureFlags,
    'feature-flags',
    undefined,
    undefined,
    false,
    {},
    true
  );
  const hasAccess = checkFeatureFlag(feature, data);
  // Report "loading" until the flags query has a definitive result. Use isPending (no data
  // yet) rather than isLoading: during IndexedDB cache restore isLoading can read false
  // while data is still undefined, and callers that act on the disabled branch (e.g.
  // <FF fallback={<Navigate/>}>) must not treat an unresolved flag as off. On a fetch error
  // isPending is false with data undefined, so callers fall through to disabled instead of
  // hanging on the loading state forever.
  return { enabled: hasAccess, isLoading: isPending };
};
